import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  TextField,
  Typography,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PhotoCamera as PhotoCameraIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Event as EventIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { activitiesApi, filesApi } from '../services/api';

const ActivityPhotoThumbnail = ({ src, alt, fallbackLabel, loading = false }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!src || hasError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          fontSize: '0.7rem',
          textAlign: 'center',
          p: 0.5,
          overflow: 'hidden',
          wordBreak: 'break-word',
        }}
      >
        {fallbackLabel}
      </Box>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }}
      onError={() => setHasError(true)}
    />
  );
};

const planDisplayName = (fileName = '') => {
  const extensionIndex = fileName.lastIndexOf('.');
  return extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
};

const ActivityCard = React.memo(({ 
  activity, 
  expandedActivity, 
  activityNotes, 
  activityPhotos, 
  newNotes, 
  selectedPhotos, 
  showMentions, 
  mentionSuggestions,
  workers,
  uploading,
  loadingNotes,
  loadingPhotos,
  completingActivity,
  onToggleComplete,
  onEdit,
  onDelete,
  onActivityExpand,
  onPhotoPreview,
  onDocumentOpen,
  onNoteChange,
  onMentionInput,
  onPhotoSelect,
  onNoteSubmit,
  onRemovePhoto,
  onInsertMention,
  onViewPlan,
  onChecklistToggle,
  isPlanLoading,
  onOpenForm,
  alwaysExpanded = false,
  hideViewPlanButton = false,
  readOnly = false,
  resolvePublicPhotoUrl = null,
}) => {
  const isExpanded = expandedActivity === activity.activity_id;
  const notes = useMemo(() => activityNotes[activity.activity_id] || [], [activity.activity_id, activityNotes]);
  const photos = useMemo(() => activityPhotos[activity.activity_id] || [], [activity.activity_id, activityPhotos]);
  const documents = activity.documents || [];
  const linkedPhotos = useMemo(() => (activity.documents || []).filter((document) => document.file_type === 'photos'), [activity.documents]);
  const linkedPlans = documents.filter((document) => document.file_type === 'plans');
  const otherDocuments = documents.filter((document) => document.file_type !== 'photos' && document.file_type !== 'plans');
  const displayedPhotos = [...photos, ...linkedPhotos];
  const linkedForms = activity.forms || [];
  const checklistItems = activity.checklist_items || [];
  const activityId = activity.activity_id;
  const currentNote = newNotes[activityId] || '';
  const currentPhotos = selectedPhotos[activityId] || [];
  const showActivityMentions = showMentions[activityId] || false;
  const activityMentionSuggestions = mentionSuggestions[activityId] || [];
  const isUploading = uploading[activityId] || false;
  const isLoadingNotes = loadingNotes[activityId] || false;
  const isLoadingPhotos = loadingPhotos[activityId] || false;
  const [photoThumbnailUrls, setPhotoThumbnailUrls] = useState({});
  const [openingPhotoKey, setOpeningPhotoKey] = useState(null);
  const [openingPlanId, setOpeningPlanId] = useState(null);
  const showNotesSection = !readOnly || isLoadingNotes || notes.length > 0;

  const openPhoto = async (photo) => {
    const key = photo.file_id ? `file-${photo.file_id}` : `photo-${photo.photo_id}`;
    if (openingPhotoKey === key) return;
    setOpeningPhotoKey(key);
    try {
      const result = onPhotoPreview?.(photo, activityId);
      if (result && typeof result.then === 'function') await result;
    } finally {
      setOpeningPhotoKey(null);
    }
  };

  const openLinkedPlan = async (plan) => {
    setOpeningPlanId(plan.file_id);
    try {
      const result = onViewPlan ? onViewPlan(activity, plan) : onDocumentOpen?.(plan);
      if (result && typeof result.then === 'function') await result;
    } finally {
      setOpeningPlanId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const resolvePhotoUrls = async () => {
      // In readOnly (share) mode, use the public resolver if provided.
      // In authenticated mode, use the standard activity photos API.
      const isPublicMode = readOnly && typeof resolvePublicPhotoUrl === 'function';
      if (readOnly && !isPublicMode) {
        return;
      }

      const allPhotos = [
        ...photos,
        ...notes.flatMap((note) => note.photos || []),
      ];

      const missingPhotos = allPhotos.filter((photo) => (
        photo?.photo_id
        && !photo?.url
        && !photoThumbnailUrls[photo.photo_id]
      ));

      if (missingPhotos.length === 0) {
        return;
      }

      const loadedUrls = {};

      await Promise.all(missingPhotos.map(async (photo) => {
        try {
          let url;
          if (isPublicMode) {
            url = await resolvePublicPhotoUrl(photo);
          } else {
            const response = await activitiesApi.getPhotoUrl(photo.photo_id);
            url = response.data?.url;
          }
          if (url) {
            loadedUrls[photo.photo_id] = url;
          }
        } catch (error) {
          console.error(`Error loading activity photo thumbnail ${photo.photo_id}:`, error);
        }
      }));

      if (!cancelled && Object.keys(loadedUrls).length > 0) {
        setPhotoThumbnailUrls((prev) => ({ ...prev, ...loadedUrls }));
      }
    };

    resolvePhotoUrls();

    return () => {
      cancelled = true;
    };
  }, [notes, photoThumbnailUrls, photos, readOnly, resolvePublicPhotoUrl]);

  useEffect(() => {
    if (readOnly) return undefined;
    let active = true;
    const missing = linkedPhotos.filter((photo) => !photo.url && photo.file_id && !photoThumbnailUrls[`file-${photo.file_id}`]);
    if (missing.length) {
      Promise.all(missing.map(async (photo) => {
        try {
          const response = await filesApi.getUrl(photo.file_id);
          return [photo.file_id, response.data?.url];
        } catch {
          return [photo.file_id, null];
        }
      })).then((results) => {
        if (!active) return;
        const urls = Object.fromEntries(results.filter(([, url]) => url).map(([id, url]) => [`file-${id}`, url]));
        if (Object.keys(urls).length) setPhotoThumbnailUrls((current) => ({ ...current, ...urls }));
      });
    }
    return () => { active = false; };
  }, [linkedPhotos, photoThumbnailUrls, readOnly]);

  const getWorkerNames = useMemo(() => {
    if (!activity.assigned_workers || activity.assigned_workers.length === 0) return 'Unassigned';
    return activity.assigned_workers.map(aw => aw.worker_name).join(', ');
  }, [activity.assigned_workers]);

  const isCompleting = completingActivity === activity.activity_id;
  const checklistCompletion = checklistItems.length > 0
    ? Math.round((checklistItems.filter((item) => item.is_completed).length / checklistItems.length) * 100)
    : null;
  const hasAssignees = Boolean(activity.assigned_workers && activity.assigned_workers.length > 0);
  const dueDate = activity.due_date ? new Date(`${String(activity.due_date).slice(0, 10)}T00:00:00`) : null;
  const hasDueDate = dueDate && !Number.isNaN(dueDate.getTime());
  const isOverdue = hasDueDate && !activity.is_completed && dueDate < new Date(new Date().setHours(0, 0, 0, 0));
  const shouldShowSummaryPills = hasAssignees || hasDueDate || checklistCompletion !== null;
  const detailSectionsVisible = alwaysExpanded || isExpanded;

  const handleCardClick = useCallback((event) => {
    if (alwaysExpanded || isExpanded || !onActivityExpand) {
      return;
    }

    if (event.target.closest('button, input, textarea, select, a, [role="button"]')) {
      return;
    }

    onActivityExpand(activity.activity_id);
  }, [activity.activity_id, alwaysExpanded, isExpanded, onActivityExpand]);

  return (
    <Card 
      id={`activity-${activity.activity_id}`}
      sx={{ 
        mb: 2, 
        bgcolor: (theme) => activity.is_completed
          ? theme.palette.background.default
          : theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }
      }}
    >
      <CardContent
        onClick={handleCardClick}
        sx={{
          pb: detailSectionsVisible ? 1 : 2,
          p: { xs: 2, md: 3 },
          cursor: alwaysExpanded || isExpanded || !onActivityExpand ? 'default' : 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: shouldShowSummaryPills ? 1.5 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: activity.description ? 0.5 : 0 }}>
                {activity.is_completed && <CheckCircleOutlineIcon color="success" fontSize="small" aria-label="Completed" />}
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, fontWeight: 500 }}
                >
                  {activity.title}
                </Typography>
              </Box>
              {activity.description && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    display: '-webkit-box',
                    WebkitLineClamp: detailSectionsVisible ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: detailSectionsVisible ? 'visible' : 'hidden',
                  }}
                >
                  {activity.description}
                </Typography>
              )}
            </Box>
          </Box>
          
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              {!alwaysExpanded && (
                <IconButton
                  size="small"
                  onClick={() => onActivityExpand(activity.activity_id)}
                  aria-label={isExpanded ? 'Collapse activity' : 'Expand activity'}
                  sx={{
                    color: 'action.active',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              )}
          </Box>
        </Box>

          {shouldShowSummaryPills && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: detailSectionsVisible ? 2 : 0, flexWrap: 'wrap' }}>
              {hasAssignees && (
                <Chip 
                  icon={<PersonIcon />}
                  label={getWorkerNames}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    borderRadius: 999,
                    fontWeight: 500
                  }}
                />
              )}
              {hasDueDate && (
                <Chip
                  icon={<EventIcon />}
                  label={`Due: ${dueDate.toLocaleDateString('en-GB')}`}
                  size="small"
                  variant="outlined"
                  color={isOverdue ? 'error' : 'default'}
                  sx={{ borderRadius: 999, fontWeight: 500 }}
                />
              )}
              {checklistCompletion !== null && (
                <Chip
                  label={`Checklist ${checklistCompletion}%`}
                  size="small"
                  color={checklistCompletion === 100 ? 'success' : 'primary'}
                  variant={checklistCompletion === 100 ? 'filled' : 'outlined'}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 600
                  }}
                />
              )}
            </Box>
          )}

          <Collapse in={detailSectionsVisible} unmountOnExit>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {!hideViewPlanButton && (activity.source_file_id || activity.SourceFileId) && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={isPlanLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <LocationOnIcon />}
                    onClick={() => onViewPlan && !isPlanLoading && onViewPlan(activity)}
                    disabled={isPlanLoading}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 1.5,
                      px: 2
                    }}
                  >
                    {isPlanLoading ? 'Opening...' : (activity.source_file_name || 'View Plan')}
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                {!readOnly && onToggleComplete && (
                  <Tooltip title={activity.is_completed ? "Completed" : "Mark as Complete"}>
                    <Button
                      size="small"
                      variant={activity.is_completed ? 'contained' : 'outlined'}
                      color={activity.is_completed ? 'success' : 'primary'}
                      startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon fontSize="small" />}
                      onClick={() => onToggleComplete(activity)}
                      disabled={activity.auto_generated || isCompleting}
                      aria-label={activity.is_completed ? 'Completed' : 'Mark as Complete'}
                      sx={{
                        mt: 0.25,
                        minWidth: { xs: 0, sm: 154 },
                        textTransform: 'none',
                        borderRadius: 999,
                        fontWeight: 600,
                        px: 1.5,
                        flexShrink: 0,
                      }}
                    >
                      {activity.is_completed ? 'Completed' : 'Complete'}
                    </Button>
                  </Tooltip>
                )}
                {onEdit && (
                  <IconButton 
                    size="small" 
                    onClick={() => onEdit(activity)}
                    sx={{ 
                      color: 'action.active',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton 
                    size="small" 
                    onClick={() => onDelete(activity)}
                    sx={{ 
                      color: 'action.active',
                      '&:hover': { bgcolor: 'error.lighter', color: 'error.main' }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {checklistItems.length > 0 && (
                <Box sx={{ mb: 2, mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: '0.85rem' }}>
                    Checklist
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {checklistItems.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1.5,
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <Checkbox
                            size="small"
                            checked={Boolean(item.is_completed)}
                            disabled={!onChecklistToggle}
                            onChange={(event) =>
                              onChecklistToggle && onChecklistToggle(activity, item.id, event.target.checked)
                            }
                            inputProps={{ 'aria-label': item.text }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {item.text}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

        {/* Activity Photos - Always Visible */}
        {displayedPhotos.length > 0 && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: '0.85rem' }}>
              Photos
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {displayedPhotos.map((photo, index) => (
                <Box
                  key={photo.file_id ? `file-${photo.file_id}` : `photo-${photo.photo_id || index}`}
                  sx={{
                    position: 'relative',
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}
                  onClick={() => void openPhoto(photo)}
                >
                  {openingPhotoKey === (photo.file_id ? `file-${photo.file_id}` : `photo-${photo.photo_id}`) && (
                    <Box role="status" aria-label="Opening photo" sx={{ position: 'absolute', inset: 0, zIndex: 1, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0, 0, 0, 0.55)' }}>
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    </Box>
                  )}
                  {(() => {
                    const resolvedSrc = photo.url || photoThumbnailUrls[photo.file_id ? `file-${photo.file_id}` : photo.photo_id]
                      || (photo.thumbnail ? `data:image/jpeg;base64,${photo.thumbnail}` : '');
                    const isLoadingUrl = !!photo.photo_id && !resolvedSrc && (!readOnly || typeof resolvePublicPhotoUrl === 'function');
                    return (
                      <ActivityPhotoThumbnail
                        src={resolvedSrc}
                        alt={photo.file_name}
                        fallbackLabel={photo.file_name}
                        loading={isLoadingUrl}
                      />
                    );
                  })()}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {linkedPlans.length > 0 && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: '0.85rem' }}>
              Plans
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {linkedPlans.map((plan) => (
                <Button key={plan.file_id} variant="outlined" size="small" startIcon={openingPlanId === plan.file_id ? <CircularProgress size={16} /> : <DescriptionIcon />}
                  onClick={() => void openLinkedPlan(plan)} disabled={openingPlanId === plan.file_id}
                  sx={{ textTransform: 'none', justifyContent: 'flex-start', borderRadius: 2, maxWidth: '100%' }}>
                  {planDisplayName(plan.file_name)}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {otherDocuments.length > 0 && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: '0.85rem' }}>
              Attachments
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {otherDocuments.map((document) => (
                <Button
                  key={document.file_id}
                  variant="outlined"
                  size="small"
                  startIcon={<DescriptionIcon />}
                  onClick={() => onDocumentOpen && onDocumentOpen(document)}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderRadius: 2,
                    maxWidth: '100%'
                  }}
                >
                  {document.file_name}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {linkedForms.length > 0 && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: '0.85rem' }}>
              Forms
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {linkedForms.map((form) => (
                <Button
                  key={form.project_form_id}
                  variant="outlined"
                  size="small"
                  startIcon={<DescriptionIcon />}
                  onClick={() => onOpenForm && onOpenForm(readOnly ? form : form.project_form_id, activity.activity_id)}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    borderRadius: 2,
                    maxWidth: '100%'
                  }}
                >
                  {form.title || form.template_name}
                </Button>
              ))}
            </Box>
          </Box>
        )}

          <Box sx={{ mt: 3 }}>
            
            {/* Loading state for both photos and notes */}
            {(isLoadingPhotos || isLoadingNotes) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {/* Activity Notes */}
                {showNotesSection && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                      Notes
                    </Typography>

                    {notes.map((note, index) => (
                      <Paper key={index} sx={{
                        p: 2.5,
                        mb: 1.5,
                        bgcolor: (theme) => theme.palette.background.paper,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Avatar sx={{
                            width: 32,
                            height: 32,
                            mr: 1.5,
                            fontSize: '0.875rem',
                            bgcolor: 'primary.main'
                          }}>
                            {note.worker_name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {note.worker_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(note.created_date).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                          {note.content}
                        </Typography>
                        {note.photos && note.photos.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                            {note.photos.map((photo, photoIndex) => (
                              <Box
                                key={photoIndex}
                                sx={{
                                  position: 'relative',
                                  width: 60,
                                  height: 60,
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  transition: 'transform 0.2s ease',
                                  '&:hover': {
                                    transform: 'scale(1.05)'
                                  }
                                }}
                                onClick={() => void openPhoto(photo)}
                              >
                                {openingPhotoKey === `photo-${photo.photo_id}` && (
                                  <Box role="status" aria-label="Opening photo" sx={{ position: 'absolute', inset: 0, zIndex: 1, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0, 0, 0, 0.55)' }}>
                                    <CircularProgress size={24} sx={{ color: 'white' }} />
                                  </Box>
                                )}
                                {(() => {
                                  const resolvedSrc = photo.url || photoThumbnailUrls[photo.photo_id] || (photo.thumbnail ? `data:image/jpeg;base64,${photo.thumbnail}` : '');
                                  const isLoadingUrl = !!photo.photo_id && !resolvedSrc && (!readOnly || typeof resolvePublicPhotoUrl === 'function');
                                  return (
                                    <ActivityPhotoThumbnail
                                      src={resolvedSrc}
                                      alt={photo.file_name}
                                      fallbackLabel={photo.file_name}
                                      loading={isLoadingUrl}
                                    />
                                  );
                                })()}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}

                    {!readOnly && onNoteSubmit && onNoteChange && onMentionInput && onPhotoSelect && onRemovePhoto && onInsertMention && (
                      <Box sx={{ position: 'relative', mt: 2 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Add a note... Use @name to mention someone"
                          value={currentNote}
                          onChange={(e) => {
                            onNoteChange(activityId, e.target.value);
                            onMentionInput(e.target.value, e.target.selectionStart, activityId);
                          }}
                          sx={{
                            mb: 1.5,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              bgcolor: 'background.paper'
                            }
                          }}
                        />

                        {showActivityMentions && (
                          <Paper
                            elevation={8}
                            sx={{
                              position: 'absolute',
                              bottom: '100%',
                              left: 0,
                              right: 0,
                              maxHeight: 200,
                              overflow: 'auto',
                              zIndex: 1000,
                              mb: 1,
                              borderRadius: 2
                            }}
                          >
                            {activityMentionSuggestions.map((worker) => (
                              <Box
                                key={worker.worker_id}
                                sx={{
                                  p: 1.5,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                  display: 'flex',
                                  alignItems: 'center',
                                  transition: 'background-color 0.2s ease'
                                }}
                                onClick={() => onInsertMention(worker, activityId)}
                              >
                                <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.875rem' }}>
                                  {worker.name?.charAt(0)}
                                </Avatar>
                                <Typography variant="body2">{worker.name}</Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id={`note-photo-upload-${activityId}`}
                            multiple
                            type="file"
                            onChange={(e) => onPhotoSelect(e, activityId)}
                          />
                          <label htmlFor={`note-photo-upload-${activityId}`}>
                            <IconButton
                              component="span"
                              size="small"
                              sx={{
                                color: 'action.active',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              <PhotoCameraIcon />
                            </IconButton>
                          </label>

                          <Button
                            size="small"
                            variant="contained"
                            startIcon={isUploading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SendIcon />}
                            onClick={() => onNoteSubmit(activityId)}
                            disabled={(!currentNote.trim() && currentPhotos.length === 0) || isUploading}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 500,
                              borderRadius: 1.5,
                              px: 2
                            }}
                          >
                            {isUploading ? 'Uploading...' : 'Send'}
                          </Button>
                        </Box>

                        {currentPhotos.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                              {currentPhotos.map((photo, index) => (
                                <Box key={index} sx={{ position: 'relative', width: 80, height: 80 }}>
                                  <img
                                    src={photo.preview}
                                    alt={photo.name}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(0,0,0,0.12)',
                                      opacity: isUploading ? 0.5 : 1
                                    }}
                                  />

                                  {isUploading && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.6)',
                                        borderRadius: '8px',
                                      }}
                                    >
                                      <CircularProgress
                                        size={28}
                                        sx={{ color: 'white' }}
                                      />
                                    </Box>
                                  )}

                                  {!isUploading && (
                                    <IconButton
                                      onClick={() => onRemovePhoto(activityId, index)}
                                      sx={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        bgcolor: 'error.main',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'error.dark' },
                                        width: 24,
                                        height: 24,
                                      }}
                                      size="small"
                                    >
                                      <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}
                            </Box>

                            {isUploading && (
                              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  Uploading {currentPhotos.length} photo{currentPhotos.length > 1 ? 's' : ''}...
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for better memoization
  const activityId = prevProps.activity.activity_id;
  
  // Check if this specific activity's data has changed
  return (
    prevProps.activity === nextProps.activity &&
    prevProps.expandedActivity === nextProps.expandedActivity &&
    prevProps.activityNotes[activityId] === nextProps.activityNotes[activityId] &&
    prevProps.activityPhotos[activityId] === nextProps.activityPhotos[activityId] &&
    prevProps.newNotes[activityId] === nextProps.newNotes[activityId] &&
    prevProps.selectedPhotos[activityId] === nextProps.selectedPhotos[activityId] &&
    prevProps.showMentions[activityId] === nextProps.showMentions[activityId] &&
    prevProps.mentionSuggestions[activityId] === nextProps.mentionSuggestions[activityId] &&
    prevProps.uploading[activityId] === nextProps.uploading[activityId] &&
    prevProps.loadingNotes[activityId] === nextProps.loadingNotes[activityId] &&
    prevProps.loadingPhotos[activityId] === nextProps.loadingPhotos[activityId] &&
    prevProps.completingActivity === nextProps.completingActivity &&
    prevProps.isPlanLoading === nextProps.isPlanLoading &&
    prevProps.workers === nextProps.workers &&
    prevProps.hideViewPlanButton === nextProps.hideViewPlanButton &&
    prevProps.readOnly === nextProps.readOnly
  );
});

export default ActivityCard;
